import type { Action } from '@reduxjs/toolkit';
import { combineReducers } from '@reduxjs/toolkit';
import type {
    CallbackEmpty,
    IReducerRegister,
    ReducerManagerEventMap,
    ReducerSlice,
    ReducerUnknown,
    SubscriberList,
} from '../interface';

const _defaultReducer: ReducerUnknown = (state, action) => state;

export default function registry(initialReducers?: Record<string, ReducerUnknown>) {
    /* Create an object which maps keys to reducers */
    const reducers: Record<string, ReducerUnknown> = { ...(initialReducers ?? {}) };

    const subscribers: SubscriberList = {
        SliceAdded: [],
        ReducersChanged: [],
    };

    /* Create the initial combinedReducer */
    let combinedReducer = initialReducers ? combineReducers(reducers) : _defaultReducer;

    /* An array which is used to delete state keys when reducers are removed */
    let keysToRemove: string[] = [];

    const manager: IReducerRegister = {
        getReducerMap: () => ({ ...reducers }),

        /* The root reducer function exposed by this object
           This will be passed to the store */
        reduce: (stateIn: Record<string, unknown>, action: Action<unknown>) => {
            let newState = stateIn;

            /* If any reducers have been removed, clean up their state first */
            if (keysToRemove.length > 0) {
                newState = { ...stateIn };
                for (const key of keysToRemove) {
                    delete newState[key];
                }
                keysToRemove = [];
            }

            /* Delegate to the combined reducer */
            return combinedReducer(newState, action);
        },

        addSlice: (slice: ReducerSlice) => {
            if (!slice.name || reducers[slice.name]) {
                return;
            }

            reducers[slice.name] = slice.reducer;

            combinedReducer = combineReducers(reducers);

            subscribers.ReducersChanged.forEach(cb => {
                cb();
            });
            subscribers.SliceAdded.forEach(cb => {
                cb(slice);
            });
        },

        /* Adds a new reducer with the specified key */
        add: (key: string, reducer: ReducerUnknown) => {
            if (!key || reducers[key]) {
                return;
            }

            /* Add the reducer to the reducer mapping */
            reducers[key] = reducer;

            /* Generate a new combined reducer */
            combinedReducer = combineReducers(reducers);

            subscribers.ReducersChanged.forEach(cb => {
                cb();
            });
        },

        /* Removes a reducer with the specified key */
        remove: (key: string) => {
            if (!key || !reducers[key]) {
                return;
            }

            /* Remove it from the reducer mapping */
            delete reducers[key];

            /* Add the key to the list of keys to clean up */
            keysToRemove.push(key);

            /* Generate a new combined reducer */
            combinedReducer = combineReducers(reducers);

            subscribers.ReducersChanged.forEach(cb => {
                cb();
            });
        },

        subscribe: <K extends keyof ReducerManagerEventMap>(
            event: K,
            callback: ReducerManagerEventMap[K],
        ) => {
            const callbacks = subscribers[event];
            callbacks.push(callback as CallbackEmpty);
            subscribers[event] = callbacks;
        },

        unsubscribe: <K extends keyof ReducerManagerEventMap>(
            event: K,
            callback: ReducerManagerEventMap[K],
        ) => {
            const callbacks = subscribers[event].filter(cb => cb !== callback);
            subscribers[event] = callbacks as SubscriberList[K];
        },
    };

    return manager;
}
